<template>
  <div class="fdle-wrap">

    <!-- Loading -->
    <div class="state-box" v-if="loadingData">
      <i class="fa fa-spinner fa-spin"></i> Loading…
    </div>

    <!-- Empty -->
    <div class="state-box" v-else-if="!loadingData && table_datas.length === 0">
      <i class="fa fa-map-marker-alt"></i> No Florida ORI rates found
    </div>

    <template v-else>
      <!-- Toolbar -->
      <div class="toolbar">
        <div class="toolbar-left">
          <span class="stat-chip blue">{{ table_datas.length }} ORI entries</span>
        </div>
        <button class="btn-refresh" @click="Refresh()">
          <i class="fa fa-sync-alt"></i> Refresh
        </button>
      </div>

      <!-- List header -->
      <div class="list-header">
        <div>ORI</div>
        <div style="text-align:right;">Cost</div>
        <div style="text-align:right;">Markup</div>
        <div style="text-align:right;">Sell Price</div>
        <div style="text-align:center;">Status</div>
        <div></div>
      </div>

      <!-- ORI rows -->
      <div
        v-for="(data, index) in table_datas"
        :key="index"
        class="ori-row"
        :class="{ 'is-expanded': !!expandedRows[index] }"
      >
        <!-- Collapsed row head -->
        <div class="ori-row-head" @click="toggleExpand(index)">
          <div class="orh-name">
            <div class="ori-icon"><i class="fa fa-map-marker-alt"></i></div>
            <span class="ori-code">{{ data.service_ori }}</span>
          </div>
          <div class="orh-val">${{ data.cost || '—' }}</div>
          <div class="orh-val">
            <span v-if="data.markup" class="val-custom">${{ data.markup }}</span>
            <span v-else class="val-fallback">${{ data.default_markup || '—' }}</span>
          </div>
          <div class="orh-val orh-sell">${{ data.selling_price || '—' }}</div>
          <div style="text-align:center;">
            <span v-if="hasCustom(data)" class="status-badge s-custom">
              <i class="fa fa-check-circle"></i> Custom
            </span>
            <span v-else class="status-badge s-default">Default</span>
          </div>
          <div class="orh-toggle">
            <i class="fa fa-chevron-down expand-icon" :class="{ rotated: expandedRows[index] }"></i>
          </div>
        </div>

        <!-- Expanded comparison panel -->
        <div class="ori-detail" v-if="expandedRows[index]">
          <div class="rate-grid">

            <!-- Reference column group -->
            <div class="rg-group ref-group">
              <div class="rg-group-head"><i class="fa fa-lock"></i> Reference</div>
              <div class="rg-row">
                <span class="rg-label">Cost</span>
                <span class="rg-val">${{ data.cost || '—' }}</span>
              </div>
              <div class="rg-row">
                <span class="rg-label">Def. Markup</span>
                <span class="rg-val">${{ data.default_markup || '—' }}</span>
              </div>
              <div class="rg-row">
                <span class="rg-label">Def. Commission</span>
                <span class="rg-val">${{ data.default_commission || '—' }}</span>
              </div>
              <div class="rg-row">
                <span class="rg-label">Def. Tax</span>
                <span class="rg-val">{{ data.default_tax || '—' }}%</span>
              </div>
            </div>

            <!-- Custom column group -->
            <div class="rg-group cus-group">
              <div class="rg-group-head"><i class="fa fa-edit"></i> Custom</div>
              <div class="rg-row">
                <span class="rg-label">Markup</span>
                <div class="input-wrap">
                  <span class="ip">$</span>
                  <input
                    class="rate-input"
                    type="number" min="0"
                    v-model="data.markup"
                    @keyup="changemarkup(index)"
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div class="rg-row">
                <span class="rg-label">Commission</span>
                <div class="input-wrap">
                  <span class="ip">$</span>
                  <input
                    class="rate-input"
                    type="number" min="0"
                    v-model="data.commission_percentage"
                    @keyup="changecommission(index)"
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div class="rg-row">
                <span class="rg-label">Tax</span>
                <div class="input-wrap">
                  <span class="ip">%</span>
                  <input
                    class="rate-input"
                    type="number" min="0"
                    v-model="data.tax_percentage"
                    @keyup="changetaxpercentage(index)"
                    placeholder="0"
                  />
                </div>
              </div>
            </div>

            <!-- Calculated column group -->
            <div class="rg-group calc-group">
              <div class="rg-group-head"><i class="fa fa-calculator"></i> Calculated</div>
              <div class="rg-row">
                <span class="rg-label">Sub Total</span>
                <span class="rg-val rg-calc">${{ data.sub_total || '—' }}</span>
              </div>
              <div class="rg-row">
                <span class="rg-label">Sell Price</span>
                <span class="rg-val rg-sell">${{ data.selling_price || '—' }}</span>
              </div>
            </div>

          </div>
        </div>
      </div>
    </template>

    <!-- Save bar -->
    <div class="save-bar" v-if="!loadingData && table_datas.length > 0">
      <div class="save-bar__inner">
        <span class="save-bar__info">
          {{ client.name ? client.name + ' — Florida ORI Rates' : 'Florida ORI Rates' }}
        </span>
        <button class="btn-save" ref="kt_save_service" @click="update('S', 'kt_save_service')">
          <i class="fa fa-check"></i> Save Rates
        </button>
      </div>
    </div>

  </div>
</template>

<script>
import { SET_BREADCRUMB } from "@/core/services/store/mutations.type";
import {
  FETCH_LOCATION_ORI_RATE_LISTS,
  UPDATE_LOCATION_ORI_RATE_LISTS
} from "@/core/services/store/actions.type";
import { mapGetters } from "vuex";

export default {
  data() {
    return {
      breadCrumbs: [
        { id: 1, title: "Dashboard", route: "client.dashboard" },
        { id: 2, title: "Locations", route: "client.location" },
        { id: 3, title: "Update Rate", route: "" }
      ],
      table_datas: [],
      errors: [],
      loadingData: false,
      serviceError: "",
      selectAll: false,
      service_ori: [],
      rates: [],
      client: [],
      set_default_markup: "",
      set_default_commission: "",
      set_default_tax: "",
      expandedRows: {}
    };
  },
  computed: {
    ...mapGetters(["getServiceOriData"])
  },
  created() {
    this.breadCrumbs[2].title = "Edit Rate";
    this.$store.commit(SET_BREADCRUMB, this.breadCrumbs);
    this.getServiceData();
  },
  methods: {
    toggleExpand(index) {
      this.$set(this.expandedRows, index, !this.expandedRows[index]);
    },
    hasCustom(data) {
      return data.markup || data.tax_percentage || data.commission_percentage;
    },
    closeBtnLoad(ref) {
      const b = this.$refs[ref];
      if (b) { b.classList.remove("spinner","spinner-light","spinner-right"); b.disabled = false; }
    },
    loadingButton(ref) {
      const b = this.$refs[ref];
      if (b) { b.disabled = true; b.classList.add("spinner","spinner-light","spinner-right"); }
    },
    getServiceData() {
      this.loadingData = true;
      this.table_datas = [];
      const data_ = { id: this.$route.params.id, code: "fdle" };
      this.$store.dispatch(FETCH_LOCATION_ORI_RATE_LISTS, data_)
        .then(data => {
          this.rates = data.rates;
          this.client = data.client;
          this.set_default_markup    = data.client.default_markup;
          this.set_default_tax       = data.client.default_tax;
          this.set_default_commission = data.client.default_commission;
          this.rates.forEach(service_ori => {
            const base = {
              id: service_ori.id,
              service_ori: service_ori.ori_number,
              checked: !!service_ori.location_service_ori_id,
              cost: service_ori.cost,
              default_markup:     service_ori.default_ori_markup             ?? "",
              default_commission: service_ori.default_ori_commission_percentage ?? "",
              default_tax:        service_ori.default_ori_tax_percentage      ?? "",
              markup:             service_ori.markup                          ?? "",
              sub_total:          service_ori.sub_total,
              tax_percentage:     service_ori.location_tax_percentage         ?? "",
              commission_percentage: service_ori.commission_percentage        ?? "",
              selling_price:      service_ori.selling_price
            };
            this.table_datas.push(base);
          });
          this.loadingData = false;
        })
        .catch(() => { this.loadingData = false; });
    },
    _calcTotals(index) {
      const d = this.table_datas[index];
      const cost       = parseFloat(d.cost || 0);
      const markup     = d.markup             ? parseFloat(d.markup)             : parseFloat(d.default_markup     || 0);
      const commission = d.commission_percentage ? parseFloat(d.commission_percentage) : parseFloat(d.default_commission || 0);
      const tax        = d.tax_percentage     ? parseFloat(d.tax_percentage)     : parseFloat(d.default_tax        || 0);
      const sub = cost + markup + commission;
      d.sub_total     = (Math.round(sub * 100) / 100).toFixed(2);
      d.selling_price = (Math.round((sub + sub * tax / 100) * 100) / 100).toFixed(2);
    },
    changemarkup(index)        { this._calcTotals(index); },
    changecommission(index)    { this._calcTotals(index); },
    changetaxpercentage(index) { this._calcTotals(index); },
    Refresh() {
      this.set_default_markup = "";
      this.set_default_tax = "";
      this.set_default_commission = "";
      this.table_datas = [];
      this.expandedRows = {};
      this.getServiceData();
    },
    update(_saveType, ref) {
      this.errors = [];
      this.loadingButton(ref);
      this.$store.dispatch(UPDATE_LOCATION_ORI_RATE_LISTS, {
        location_id:        this.$route.params.id,
        code:               this.client.service_type,
        default_rate:       "",
        default_tax:        "",
        default_commission: "",
        rate_list:          this.table_datas
      })
        .then(data => {
          this.$toastr.s(data.msg);
          this.closeBtnLoad(ref);
          this.getServiceData();
        })
        .catch(err => {
          this.errors = err;
          this.selectAll = false;
          this.serviceError = "";
          this.closeBtnLoad(ref);
        });
    }
  }
};
</script>

<style lang="scss" scoped>
$font:    'Segoe UI', sans-serif;
$blue:    #389ad4;
$dark:    #0d1b3e;
$green:   #276749;
$purple:  #6b21a8;
$border:  #dde2ec;

/* ── Toolbar ── */
.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 20px;
  border-bottom: 1px solid #f0f2f8;
  background: #fafbfd;

  .toolbar-left { display: flex; gap: 8px; align-items: center; }

  .stat-chip {
    padding: 3px 10px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;

    &.blue { background: #dbeafe; color: #1d4ed8; }
  }

  .btn-refresh {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 30px;
    padding: 0 14px;
    background: #fff;
    color: #4a5568;
    border: 1.5px solid $border;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 600;
    font-family: $font;
    cursor: pointer;
    transition: all 0.15s;

    &:hover { border-color: $blue; color: $blue; }
  }
}

/* ── List header ── */
.list-header {
  display: grid;
  grid-template-columns: 1fr 100px 130px 120px 100px 36px;
  align-items: center;
  padding: 8px 20px;
  background: #fafbfd;
  border-bottom: 2px solid #eef0f8;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #a0aab8;
  gap: 8px;
}

/* ── ORI row ── */
.ori-row {
  border-bottom: 1px solid #f5f7fa;

  &:last-child { border-bottom: none; }

  &.is-expanded > .ori-row-head {
    background: #f8fbff;
    border-bottom: 1px solid #e8edf5;
  }
}

.ori-row-head {
  display: grid;
  grid-template-columns: 1fr 100px 130px 120px 100px 36px;
  align-items: center;
  padding: 10px 20px;
  cursor: pointer;
  gap: 8px;
  transition: background 0.12s;

  &:hover { background: #f8fbff; }
}

.orh-name {
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: 0;

  .ori-icon {
    width: 28px;
    height: 28px;
    border-radius: 7px;
    background: #ede9fe;
    color: $purple;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    flex-shrink: 0;
  }

  .ori-code {
    font-size: 13px;
    font-weight: 400;
    color: $dark;
    font-family: monospace;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.orh-val {
  text-align: right;
  font-size: 12px;
  font-family: monospace;
  font-weight: 600;
  color: #4a5568;

  .val-custom  { color: $green; }
  .val-fallback { color: #a0aab8; }

  &.orh-sell { color: $blue; }
}

.orh-toggle {
  display: flex;
  justify-content: center;

  .expand-icon {
    font-size: 11px;
    color: #a0aab8;
    transition: transform 0.2s;
    &.rotated { transform: rotate(180deg); }
  }
}

/* ── Status badges ── */
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 9px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;

  &.s-custom  { background: #dcfce7; color: $green; }
  &.s-default { background: #e2e8f0; color: #718096; }
}

/* ── Expanded panel ── */
.ori-detail {
  background: #f9fbfd;
  border-top: 1px solid #eef0f8;
  padding: 14px 20px 14px 57px;
}

/* ── Rate grid (column groups) ── */
.rate-grid {
  display: flex;
  gap: 12px;
  max-width: 720px;
}

.rg-group {
  flex: 1;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #e8edf5;

  .rg-group-head {
    padding: 6px 12px;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    display: flex;
    align-items: center;
    gap: 5px;
  }

  .rg-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 7px 12px;
    border-top: 1px solid #f5f7fa;
    gap: 8px;

    &:hover { background: rgba(255,255,255,0.6); }

    .rg-label {
      font-size: 11px;
      font-weight: 600;
      color: #4a5568;
      white-space: nowrap;
    }

    .rg-val {
      font-size: 12px;
      font-family: monospace;
      font-weight: 600;
      color: #7a8499;

      &.rg-calc { color: #6b21a8; }
      &.rg-sell { color: $blue; font-size: 13px; }
    }
  }
}

.ref-group {
  background: #f0f6ff;
  .rg-group-head { background: #dbeafe; color: #1d4ed8; }
}

.cus-group {
  background: #f0fdf4;
  .rg-group-head { background: #dcfce7; color: $green; }
}

.calc-group {
  background: #faf5ff;
  .rg-group-head { background: #ede9fe; color: $purple; }
}

/* ── Input ── */
.input-wrap {
  display: flex;
  align-items: center;
  height: 28px;
  border: 1.5px solid $border;
  border-radius: 6px;
  background: #fff;
  overflow: hidden;
  transition: border-color 0.15s;
  width: 110px;

  &:focus-within { border-color: $blue; box-shadow: 0 0 0 2px rgba(56,154,212,0.1); }

  .ip {
    height: 100%;
    padding: 0 5px 0 7px;
    background: #f5f7fa;
    border-right: 1px solid #e2e8f0;
    color: #a0aab8;
    font-size: 10px;
    font-weight: 600;
    display: flex;
    align-items: center;
    flex-shrink: 0;
    user-select: none;
  }

  .rate-input {
    flex: 1;
    border: none;
    outline: none;
    height: 100%;
    padding: 0 6px;
    font-size: 12px;
    color: $dark;
    font-family: $font;
    background: transparent;
    text-align: right;
    min-width: 0;

    &[type=number] { -moz-appearance: textfield; appearance: textfield; }
    &::-webkit-outer-spin-button, &::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
  }
}

/* ── States ── */
.state-box {
  text-align: center;
  padding: 56px 24px;
  color: #a0aab8;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

/* ── Save bar ── */
.save-bar {
  position: fixed;
  bottom: 2.5rem;
  width: calc(78vw);
  z-index: 200;

  .save-bar__inner {
    background: $dark;
    border-radius: 12px;
    box-shadow: 0 8px 32px rgba(13,27,62,0.3);
    padding: 14px 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;

    .save-bar__info {
      font-size: 13px;
      color: rgba(255,255,255,0.75);
      font-family: $font;
    }

    .btn-save {
      display: inline-flex;
      align-items: center;
      gap: 7px;
      height: 36px;
      padding: 0 22px;
      background: $green;
      color: #fff;
      border: none;
      border-radius: 8px;
      font-size: 13px;
      font-weight: 600;
      font-family: $font;
      cursor: pointer;
      transition: opacity 0.2s;

      &:hover { opacity: 0.9; }
      &:disabled { opacity: 0.55; cursor: not-allowed; }
    }
  }
}
</style>
